import { useUrlGenerator } from '@folklore/routes';
import { useMemo } from 'react';

import { formatDurationIso8601 } from '../lib/utils';

import {
    useAuthorUrlGenerator,
    useCanonicalUrl,
    useCollectionUrlGenerator,
    useDocumentUrlGenerator,
} from './useUrlGenerator';

export function useAuthorSchema(author: Author | null) {
    const url = useAuthorUrlGenerator();
    const canonicalUrl = useCanonicalUrl();
    if (author === null) {
        return null;
    }
    const {
        name,
        shortDescription = null,
        slug = null,
        image = null,
        title = null,
        isPerson = true,
    } = author || {};
    const { url: imageUrl = null } = image || {};
    const pageUrl = url({
        slug,
    });
    return isPerson
        ? {
              '@context': 'http://schema.org/',
              '@type': 'Person',
              identifier: canonicalUrl(pageUrl),
              name,
              jobTitle: title,
              image: imageUrl,
              description: shortDescription,
              url: canonicalUrl(pageUrl),
          }
        : null;
}

export function useVideoSchema(video: Video | null) {
    if (video === null) {
        return null;
    }
    const { url = null, width = null, height = null, duration = null, embed = null } = video || {};
    const { iframeUrl = null } = embed || {};
    return {
        '@context': 'http://schema.org/',
        '@type': 'Video',
        identifier: url,
        width,
        height,
        embed: iframeUrl,
        duration: formatDurationIso8601(duration),
    };
}

export function useDocumentSchema(document: FullDocument | null) {
    const documentUrl = useDocumentUrlGenerator();
    const collectionUrl = useCollectionUrlGenerator();
    const url = useUrlGenerator();
    const canonicalUrl = useCanonicalUrl();
    const {
        type = null,
        title = null,
        metadata: {
            credits = null,
            authors = null,
            createdAt = null,
            publishedAt = null,
            modifiedAt = null,
            description = null,
            collections = null,
            episode = null,
        } = {},
        video = null,
        image = null,
    } = document || {};
    const [{ author: creditAuthor = null } = {}] = credits || [];
    const [author = creditAuthor] = authors || [];
    const authorSchema = useAuthorSchema(author);
    const videoSchema = useVideoSchema(video);
    if (document === null) {
        return null;
    }
    const serie = (collections || []).reduce((acc, coll) => {
        const { serie = null, type: collectionType = null } = coll || {};
        if (collectionType.indexOf('serie') !== -1) {
            return coll || acc;
        }
        return serie || acc;
    }, null);
    const season = (collections || []).reduce((acc, coll) => {
        const { type: collectionType = null } = coll || {};
        return collectionType.indexOf('season') !== -1 ? coll : acc;
    }, null);
    const { type: serieType = null, image: serieImage } = serie || {};
    const { type: seasonType = null } = season || {};
    const { url: serieThumbnailUrl = null } = serieImage || {};
    const pageUrl = documentUrl(document);
    const { url: thumbnailUrl = null } = image || {};
    const director =
        (credits || []).find(
            ({ role = null }) => role === 'Réalisateur' || role === 'Réalisation',
        ) || null;
    let schemaType = 'Article';
    if ((type === 'video' || type === 'podcast') && serieType === 'serie_podcast') {
        schemaType = 'PodcastEpisode';
    } else if (type === 'video' && serieType === 'serie') {
        schemaType = 'TVEpisode';
    }
    return {
        '@context': 'http://schema.org/',
        '@type': schemaType,
        identifier: canonicalUrl(pageUrl),
        url: canonicalUrl(pageUrl),
        name: title,
        description,
        thumbnailUrl,
        video: videoSchema,
        author: authorSchema,
        dateCreated: createdAt,
        dateModified: modifiedAt,
        datePublished: publishedAt,
        director:
            director !== null
                ? {
                      '@type': 'Person',
                      name: director.name,
                  }
                : null,
        ...(serie !== null
            ? {
                  episodeNumber: episode,
                  partOfSeries: {
                      '@type': serieType === 'serie_podcast' ? 'PodcastSeries' : 'TVSeries',
                      name: serie.title || null,
                      identifier: canonicalUrl(collectionUrl(serie)),
                      url: canonicalUrl(collectionUrl(serie)),
                      thumbnailUrl: serieThumbnailUrl,
                  },
              }
            : null),
        ...(season !== null
            ? {
                  partOfSeason: {
                      '@type': seasonType === 'season_podcast' ? 'PodcastSeason' : 'TVSeason',
                      name: season.title || null,
                      identifier: `${canonicalUrl(collectionUrl(serie))}#${season.slug}`,
                      url: canonicalUrl(collectionUrl(serie)),
                  },
              }
            : null),
    };
}

export function useCollectionSchemas(collection: Collection | null) {
    const collectionUrl = useCollectionUrlGenerator();
    const canonicalUrl = useCanonicalUrl();
    const {
        type = null,
        title = null,
        metadata: {
            credits = null,
            authors = null,
            createdAt = null,
            publishedAt = null,
            modifiedAt = null,
            description = null,
            itemsCount = 0,
        } = {},
        trailer = null,
        seasons = null,
        image = null,
    } = collection || {};
    const [{ author: creditAuthor = null } = {}] = credits || [];
    const [author = creditAuthor] = authors || [];
    const authorSchema = useAuthorSchema(author);
    const trailerSchema = useVideoSchema(trailer);
    if (type === null || type.indexOf('serie') === -1) {
        return null;
    }
    const director =
        (credits || []).find(
            ({ role = null }) => role === 'Réalisateur' || role === 'Réalisation',
        ) || null;
    const isPodcast = type.indexOf('podcast') !== -1;
    const pageUrl = collectionUrl(collection);
    const { url: thumbnailUrl = null } = image || {};
    return [
        {
            '@context': 'http://schema.org/',
            '@type': isPodcast ? 'PodcastSeries' : 'TVSeries',
            identifier: canonicalUrl(pageUrl),
            url: canonicalUrl(pageUrl),
            name: title,
            description,
            thumbnailUrl,
            director:
                director !== null
                    ? {
                          '@type': 'Person',
                          name: director.name,
                      }
                    : null,
            author: authorSchema,
            trailer: trailerSchema,
            dateCreated: createdAt,
            dateModified: modifiedAt,
            datePublished: publishedAt,
            numberOfSeasons: (seasons || []).length || 1,
            numberOfEpisodes: (seasons || []).reduce(
                (acc, { itemsCount: seasonItemsCount = 0 }) => acc + (seasonItemsCount || 0),
                itemsCount,
            ),
        },
    ];
}
